import type { SummarySectionDefinition } from "../../lib/formSubmissionSummary";

export type ConfiguredFieldType =
  | "text"
  | "email"
  | "tel"
  | "date"
  | "select"
  | "textarea"
  | "radio"
  | "checkboxGroup"
  | "checkbox"
  | "file"
  | "notice";

export interface ConfiguredField {
  type: ConfiguredFieldType;
  name?: string;
  label?: string;
  required?: boolean;
  helperText?: string;
  options?: string[];
  rows?: number;
  className?: string;
  accept?: string;
  multiple?: boolean;
  autoComplete?: string;
  content?: string[];
}

export interface ConfiguredFormSection {
  id: string;
  title: string;
  fields: ConfiguredField[];
}

export interface ConfiguredServiceForm {
  formKey: string;
  formPath: string;
  formTitle: string;
  setupTitle: string;
  eyebrow: string;
  heroHeading: string;
  heroSubheading: string;
  metaDescription: string;
  submitLabel: string;
  requiredMessage: string;
  thankYouMessage: string;
  subjectNameField: string;
  clientNameField?: string;
  saveEmailField?: string;
  eventNameLabel?: string;
  eventDateLabel?: string;
  eventNameField: string;
  eventDateField?: string;
  sections: ConfiguredFormSection[];
  summarySections: SummarySectionDefinition[];
  requiredLabels: Record<string, string>;
}

const contactFields: ConfiguredField[] = [
  { type: "text", name: "fullName", label: "Full Name", required: true, autoComplete: "name" },
  { type: "email", name: "email", label: "Email Address", required: true, autoComplete: "email" },
  { type: "tel", name: "phone", label: "Phone Number", required: true, autoComplete: "tel" },
  { type: "select", name: "preferredContactMethod", label: "Preferred Contact Method", options: ["Email", "Phone", "Text"] },
  {
    type: "notice",
    content: ["Provide an additional contact person if someone else is helping coordinate the project."],
    className: "md:col-span-2",
  },
  { type: "text", name: "additionalContactName", label: "Additional Contact Name", autoComplete: "name" },
  { type: "tel", name: "additionalContactPhone", label: "Additional Contact Phone Number", autoComplete: "tel" },
  { type: "email", name: "additionalContactEmail", label: "Additional Contact Email Address", autoComplete: "email" },
];

const contactSummaryFields = [
  { name: "fullName", label: "Full Name" },
  { name: "email", label: "Email Address" },
  { name: "phone", label: "Phone Number" },
  { name: "preferredContactMethod", label: "Preferred Contact Method" },
  { name: "additionalContactName", label: "Additional Contact Name" },
  { name: "additionalContactPhone", label: "Additional Contact Phone Number" },
  { name: "additionalContactEmail", label: "Additional Contact Email Address" },
];

const finalConfirmationFields: ConfiguredField[] = [
  {
    type: "checkbox",
    name: "accurateConfirmation",
    label: "I confirm that the information provided is accurate to the best of my knowledge.",
    required: true,
  },
  {
    type: "checkbox",
    name: "contentPermission",
    label: "I confirm that I have permission to submit and use any names, stories, photos, videos, music notes, wording, links, or files provided for this project.",
    required: true,
  },
  {
    type: "checkbox",
    name: "serviceUseConfirmation",
    label: "I understand Kreatethedate will use the submitted information to create my requested service.",
    required: true,
  },
  {
    type: "checkbox",
    name: "changeRequestAcknowledgement",
    label: "I understand that major changes requested after setup has started may require additional time and/or additional fees.",
    required: true,
  },
];

const finalConfirmationSummaryFields = [
  { name: "accurateConfirmation", label: "I confirm that the information provided is accurate to the best of my knowledge.", type: "checkbox" as const },
  {
    name: "contentPermission",
    label: "I confirm that I have permission to submit and use any names, stories, photos, videos, music notes, wording, links, or files provided for this project.",
    type: "checkbox" as const,
  },
  {
    name: "serviceUseConfirmation",
    label: "I understand Kreatethedate will use the submitted information to create my requested service.",
    type: "checkbox" as const,
  },
  {
    name: "changeRequestAcknowledgement",
    label: "I understand that major changes requested after setup has started may require additional time and/or additional fees.",
    type: "checkbox" as const,
  },
];

const standardRequiredLabels = {
  fullName: "Full Name is required.",
  email: "Email Address is required.",
  phone: "Phone Number is required.",
  accurateConfirmation: "Please confirm that the information provided is accurate.",
  contentPermission: "Please confirm that you have permission to submit and use the provided content.",
  serviceUseConfirmation: "Please confirm that Kreatethedate may use the submitted information for this service.",
  changeRequestAcknowledgement: "Please confirm that you understand major setup changes may require additional time and/or fees.",
};

export const signatureSongFormConfig: ConfiguredServiceForm = {
  formKey: "signature-song",
  formPath: "/signature-song",
  formTitle: "Signature Song Form",
  setupTitle: "Signature Song Setup Form",
  eyebrow: "Signature Song Form",
  heroHeading: "Let's Create Your Signature Song",
  heroSubheading:
    "Share the story, tone, names, memories, music direction, and special details Kreatethedate should consider while creating your custom song.",
  metaDescription: "Complete the Kreatethedate Signature Song Form with story details, music preferences, deadlines, and confirmations.",
  submitLabel: "Submit Signature Song Form",
  requiredMessage: "Please complete all required fields before submitting your Signature Song Form.",
  thankYouMessage:
    "Your Kreatethedate Signature Song Form has been received. We will review your details and follow up with next steps.",
  subjectNameField: "songRecipient",
  eventNameField: "songRecipient",
  eventDateField: "eventDate",
  requiredLabels: {
    ...standardRequiredLabels,
    songRecipient: "Song Recipient or Honoree Name is required.",
    songPurpose: "Please select the purpose of this Signature Song.",
    songMood: "Please select the emotional direction for the song.",
    desiredDeliveryDate: "Desired delivery date is required.",
  },
  sections: [
    { id: "contact", title: "Client Contact Information", fields: contactFields },
    {
      id: "song-project",
      title: "Song Project Details",
      fields: [
        { type: "text", name: "songRecipient", label: "Song Recipient or Honoree Name", required: true, className: "md:col-span-2" },
        { type: "text", name: "eventType", label: "Occasion or Event Type" },
        { type: "date", name: "eventDate", label: "Event Date" },
        { type: "date", name: "desiredDeliveryDate", label: "Desired Delivery Date", required: true },
        {
          type: "radio",
          name: "songPurpose",
          label: "What is the main purpose of this Signature Song?",
          required: true,
          options: ["Gift", "Wedding", "Birthday", "Memorial / Tribute", "Graduation", "Anniversary", "Business or Organization", "Other"],
          className: "md:col-span-2",
        },
      ],
    },
    {
      id: "story-details",
      title: "Story & Meaning",
      fields: [
        { type: "textarea", name: "storyOverview", label: "Tell us the story or message behind this song.", rows: 7, className: "md:col-span-2" },
        { type: "textarea", name: "importantPeople", label: "Important people, names, relationships, or pronunciation notes", rows: 5, className: "md:col-span-2" },
        { type: "textarea", name: "keyMemories", label: "Specific memories, milestones, phrases, traditions, or inside details to include", rows: 7, className: "md:col-span-2" },
      ],
    },
    {
      id: "music-direction",
      title: "Music Direction",
      fields: [
        {
          type: "radio",
          name: "songMood",
          label: "How should the song feel?",
          required: true,
          options: ["Emotional", "Joyful", "Romantic", "Inspirational", "Soulful", "Spiritual", "Fun", "Elegant", "Not sure yet"],
          className: "md:col-span-2",
        },
        {
          type: "checkboxGroup",
          name: "musicStyles",
          label: "Preferred music styles",
          options: ["R&B", "Gospel", "Pop", "Soul", "Country", "Jazz", "Hip-Hop", "Acoustic", "Ballad", "Let Kreatethedate recommend"],
          className: "md:col-span-2",
        },
        { type: "textarea", name: "songReferences", label: "Reference songs, artists, or styles for inspiration", rows: 5, className: "md:col-span-2" },
        { type: "textarea", name: "thingsToAvoid", label: "Anything you do not want included?", rows: 5, className: "md:col-span-2" },
      ],
    },
    {
      id: "uploads",
      title: "Uploads / Inspiration Files",
      fields: [
        {
          type: "notice",
          content: ["Upload any reference notes, wording files, photos, or inspiration files you would like Kreatethedate to consider."],
          className: "md:col-span-2",
        },
        { type: "file", name: "songInspirationFiles", label: "Inspiration files", multiple: true, className: "md:col-span-2" },
      ],
    },
    {
      id: "creative-ideas",
      title: "Creative Ideas",
      fields: [
        {
          type: "textarea",
          name: "creativeIdeas",
          label: "Any Creative Ideas You Would Like Kreatethedate to Consider?",
          helperText: "Share any song ideas, surprise moments, emotional details, dedications, family traditions, or unique touches.",
          rows: 8,
          className: "md:col-span-2",
        },
      ],
    },
    {
      id: "future-services",
      title: "Future Services Interest",
      fields: [
        {
          type: "checkboxGroup",
          name: "futureServicesInterest",
          label: "Would you like information about additional Kreatethedate services?",
          options: ["RSVP Hub", "Memory Hub", "Archive Hub", "Memory Media Vault", "Signature Highlight Reel", "Not at this time"],
          className: "md:col-span-2",
        },
      ],
    },
    { id: "confirmation", title: "Permission & Confirmation", fields: finalConfirmationFields },
  ],
  summarySections: [
    { title: "Client Contact Information", fields: contactSummaryFields },
    {
      title: "Song Project Details",
      fields: [
        { name: "songRecipient", label: "Song Recipient or Honoree Name" },
        { name: "eventType", label: "Occasion or Event Type" },
        { name: "eventDate", label: "Event Date", type: "date" },
        { name: "desiredDeliveryDate", label: "Desired Delivery Date", type: "date" },
        { name: "songPurpose", label: "What is the main purpose of this Signature Song?" },
      ],
    },
    {
      title: "Story & Meaning",
      fields: [
        { name: "storyOverview", label: "Story or message behind this song" },
        { name: "importantPeople", label: "Important people, names, relationships, or pronunciation notes" },
        { name: "keyMemories", label: "Specific memories, milestones, phrases, traditions, or inside details" },
      ],
    },
    {
      title: "Music Direction",
      fields: [
        { name: "songMood", label: "How should the song feel?" },
        { name: "musicStyles", label: "Preferred music styles", type: "checkboxGroup" },
        { name: "songReferences", label: "Reference songs, artists, or styles" },
        { name: "thingsToAvoid", label: "Anything to avoid" },
      ],
    },
    { title: "Uploads / Inspiration Files", fields: [{ name: "songInspirationFiles", label: "Inspiration files", type: "file" }] },
    { title: "Creative Ideas", fields: [{ name: "creativeIdeas", label: "Any Creative Ideas You Would Like Kreatethedate to Consider?" }] },
    { title: "Future Services Interest", fields: [{ name: "futureServicesInterest", label: "Would you like information about additional Kreatethedate services?", type: "checkboxGroup" }] },
    { title: "Permission & Confirmation", fields: finalConfirmationSummaryFields },
  ],
};

export const signatureHighlightReelFormConfig: ConfiguredServiceForm = {
  formKey: "signature-highlight-reel",
  formPath: "/signature-highlight-reel",
  formTitle: "Signature Highlight Reel Form",
  setupTitle: "Signature Highlight Reel Setup Form",
  eyebrow: "Signature Highlight Reel Form",
  heroHeading: "Let's Create Your Signature Highlight Reel",
  heroSubheading:
    "Share your event details, footage notes, storytelling direction, music preferences, and delivery needs so we can shape a beautiful highlight reel.",
  metaDescription: "Complete the Kreatethedate Signature Highlight Reel Form with event details, video direction, footage notes, and confirmations.",
  submitLabel: "Submit Signature Highlight Reel Form",
  requiredMessage: "Please complete all required fields before submitting your Signature Highlight Reel Form.",
  thankYouMessage:
    "Your Kreatethedate Signature Highlight Reel Form has been received. We will review your details and follow up with next steps.",
  subjectNameField: "eventName",
  eventNameField: "eventName",
  eventDateField: "eventDate",
  requiredLabels: {
    ...standardRequiredLabels,
    eventType: "Event Type is required.",
    eventName: "Event Name or Honoree Name is required.",
    eventDate: "Event Date is required.",
    reelPurpose: "Please select the main purpose of this highlight reel.",
    reelStyle: "Please select the preferred highlight reel style.",
    desiredDeliveryDate: "Desired delivery date is required.",
  },
  sections: [
    { id: "contact", title: "Client Contact Information", fields: contactFields },
    {
      id: "event-details",
      title: "Event / Project Details",
      fields: [
        { type: "text", name: "eventType", label: "Event Type", required: true },
        { type: "text", name: "eventName", label: "Event Name or Honoree Name", required: true },
        { type: "date", name: "eventDate", label: "Event Date", required: true },
        { type: "date", name: "desiredDeliveryDate", label: "Desired Delivery Date", required: true },
        { type: "text", name: "eventLocation", label: "Event Location", className: "md:col-span-2" },
        { type: "textarea", name: "projectDescription", label: "Short project description", rows: 5, className: "md:col-span-2" },
      ],
    },
    {
      id: "reel-purpose",
      title: "Highlight Reel Purpose",
      fields: [
        {
          type: "radio",
          name: "reelPurpose",
          label: "What is the main purpose of this highlight reel?",
          required: true,
          options: ["Event recap", "Tribute / Memorial", "Graduation story", "Wedding story", "Business or organization recap", "Social media feature", "Other"],
          className: "md:col-span-2",
        },
        { type: "textarea", name: "reelAudience", label: "Who will watch or receive this highlight reel?", rows: 5, className: "md:col-span-2" },
      ],
    },
    {
      id: "footage-media",
      title: "Footage & Media",
      fields: [
        {
          type: "notice",
          content: ["Kreatethedate will provide separate upload instructions if large video or media files are needed."],
          className: "md:col-span-2",
        },
        { type: "radio", name: "footageAvailable", label: "Do you already have photos or video footage?", options: ["Yes", "No", "Some", "Not sure yet"], className: "md:col-span-2" },
        { type: "file", name: "highlightMediaFiles", label: "Reference files or sample clips", multiple: true, className: "md:col-span-2" },
        { type: "textarea", name: "importantMoments", label: "Important moments, people, speeches, scenes, or clips to include", rows: 7, className: "md:col-span-2" },
        { type: "textarea", name: "mediaInstructions", label: "Additional media instructions", rows: 5, className: "md:col-span-2" },
      ],
    },
    {
      id: "style-direction",
      title: "Style & Music Direction",
      fields: [
        {
          type: "radio",
          name: "reelStyle",
          label: "How should the highlight reel feel?",
          required: true,
          options: ["Elegant", "Emotional", "Fun", "Modern", "Luxury", "High-energy", "Spiritual", "Documentary-style", "Not sure yet"],
          className: "md:col-span-2",
        },
        { type: "checkboxGroup", name: "musicPreferences", label: "Music preferences", options: ["Instrumental", "Gospel", "R&B", "Pop", "Cinematic", "Upbeat", "Soft emotional", "Let Kreatethedate recommend"], className: "md:col-span-2" },
        { type: "textarea", name: "captionText", label: "Names, captions, titles, or wording to include", rows: 5, className: "md:col-span-2" },
        { type: "textarea", name: "thingsToAvoid", label: "Anything you do not want included?", rows: 5, className: "md:col-span-2" },
      ],
    },
    {
      id: "creative-ideas",
      title: "Creative Ideas",
      fields: [
        {
          type: "textarea",
          name: "creativeIdeas",
          label: "Any Creative Ideas You Would Like Kreatethedate to Consider?",
          helperText: "Share any tribute ideas, surprise reveals, transitions, family traditions, memory concepts, or unique touches.",
          rows: 8,
          className: "md:col-span-2",
        },
      ],
    },
    {
      id: "future-services",
      title: "Future Services Interest",
      fields: [
        {
          type: "checkboxGroup",
          name: "futureServicesInterest",
          label: "Would you like information about additional Kreatethedate services?",
          options: ["RSVP Hub", "Memory Hub", "Archive Hub", "Memory Media Vault", "Signature Song", "Not at this time"],
          className: "md:col-span-2",
        },
      ],
    },
    { id: "confirmation", title: "Permission & Confirmation", fields: finalConfirmationFields },
  ],
  summarySections: [
    { title: "Client Contact Information", fields: contactSummaryFields },
    {
      title: "Event / Project Details",
      fields: [
        { name: "eventType", label: "Event Type" },
        { name: "eventName", label: "Event Name or Honoree Name" },
        { name: "eventDate", label: "Event Date", type: "date" },
        { name: "desiredDeliveryDate", label: "Desired Delivery Date", type: "date" },
        { name: "eventLocation", label: "Event Location" },
        { name: "projectDescription", label: "Short project description" },
      ],
    },
    { title: "Highlight Reel Purpose", fields: [{ name: "reelPurpose", label: "Main purpose" }, { name: "reelAudience", label: "Audience" }] },
    {
      title: "Footage & Media",
      fields: [
        { name: "footageAvailable", label: "Do you already have photos or video footage?" },
        { name: "highlightMediaFiles", label: "Reference files or sample clips", type: "file" },
        { name: "importantMoments", label: "Important moments, people, speeches, scenes, or clips" },
        { name: "mediaInstructions", label: "Additional media instructions" },
      ],
    },
    {
      title: "Style & Music Direction",
      fields: [
        { name: "reelStyle", label: "How should the highlight reel feel?" },
        { name: "musicPreferences", label: "Music preferences", type: "checkboxGroup" },
        { name: "captionText", label: "Names, captions, titles, or wording" },
        { name: "thingsToAvoid", label: "Anything to avoid" },
      ],
    },
    { title: "Creative Ideas", fields: [{ name: "creativeIdeas", label: "Any Creative Ideas You Would Like Kreatethedate to Consider?" }] },
    { title: "Future Services Interest", fields: [{ name: "futureServicesInterest", label: "Would you like information about additional Kreatethedate services?", type: "checkboxGroup" }] },
    { title: "Permission & Confirmation", fields: finalConfirmationSummaryFields },
  ],
};

export const partnerReferralFormConfig: ConfiguredServiceForm = {
  formKey: "partner-referral",
  formPath: "/partner-referral",
  formTitle: "Partner Referral Form",
  setupTitle: "Partner Referral Form",
  eyebrow: "Partner Referral Form",
  heroHeading: "Submit a Kreatethedate Partner Referral",
  heroSubheading:
    "Share referral details, service interests, contact information, and any context Kreatethedate should know before following up.",
  metaDescription: "Complete the Kreatethedate Partner Referral Form with referrer details, referral contact information, service interests, and permissions.",
  submitLabel: "Submit Partner Referral Form",
  requiredMessage: "Please complete all required fields before submitting your Partner Referral Form.",
  thankYouMessage:
    "Your Kreatethedate Partner Referral Form has been received. We will review your referral and follow up with next steps.",
  subjectNameField: "referredName",
  eventNameField: "referredName",
  requiredLabels: {
    ...standardRequiredLabels,
    referrerName: "Your Name is required.",
    referrerEmail: "Your Email Address is required.",
    referredName: "Referral Name is required.",
    referredEmail: "Referral Email Address is required.",
    referralPermission: "Please confirm that you have permission to share this referral information.",
  },
  sections: [
    {
      id: "referrer",
      title: "Your Contact Information",
      fields: [
        { type: "text", name: "referrerName", label: "Your Name", required: true, autoComplete: "name" },
        { type: "email", name: "referrerEmail", label: "Your Email Address", required: true, autoComplete: "email" },
        { type: "tel", name: "referrerPhone", label: "Your Phone Number", autoComplete: "tel" },
        { type: "text", name: "companyName", label: "Company / Organization Name" },
        { type: "select", name: "preferredContactMethod", label: "Preferred Contact Method", options: ["Email", "Phone", "Text"] },
      ],
    },
    {
      id: "referral-contact",
      title: "Referral Contact Information",
      fields: [
        { type: "text", name: "referredName", label: "Referral Name", required: true },
        { type: "email", name: "referredEmail", label: "Referral Email Address", required: true },
        { type: "tel", name: "referredPhone", label: "Referral Phone Number" },
        { type: "text", name: "referredCompany", label: "Referral Company / Organization" },
        { type: "textarea", name: "relationshipToReferral", label: "How do you know this person or organization?", rows: 5, className: "md:col-span-2" },
      ],
    },
    {
      id: "service-interest",
      title: "Service Interest",
      fields: [
        {
          type: "checkboxGroup",
          name: "serviceInterest",
          label: "Which Kreatethedate services may interest this referral?",
          options: ["RSVP Hub", "Memory Hub", "Archive Hub", "Memory Media Vault", "Signature Song", "Signature Highlight Reel", "Not sure yet"],
          className: "md:col-span-2",
        },
        { type: "text", name: "eventType", label: "Event or Project Type" },
        { type: "date", name: "eventDate", label: "Event Date, if known" },
        { type: "textarea", name: "referralContext", label: "Tell us why this referral may be a good fit for Kreatethedate.", rows: 6, className: "md:col-span-2" },
      ],
    },
    {
      id: "follow-up",
      title: "Follow-Up Preferences",
      fields: [
        { type: "radio", name: "followUpTiming", label: "When should Kreatethedate follow up?", options: ["As soon as possible", "Within a week", "After I introduce you", "Not sure yet"], className: "md:col-span-2" },
        { type: "textarea", name: "followUpInstructions", label: "Any follow-up notes or introduction instructions?", rows: 6, className: "md:col-span-2" },
      ],
    },
    {
      id: "creative-ideas",
      title: "Additional Notes",
      fields: [
        { type: "textarea", name: "additionalNotes", label: "Anything else Kreatethedate should know about this referral?", rows: 7, className: "md:col-span-2" },
      ],
    },
    {
      id: "confirmation",
      title: "Permission & Confirmation",
      fields: [
        {
          type: "checkbox",
          name: "referralPermission",
          label: "I confirm that I have permission to share this referral information with Kreatethedate.",
          required: true,
        },
        {
          type: "checkbox",
          name: "accurateConfirmation",
          label: "I confirm that the information provided is accurate to the best of my knowledge.",
          required: true,
        },
      ],
    },
  ],
  summarySections: [
    {
      title: "Your Contact Information",
      fields: [
        { name: "referrerName", label: "Your Name" },
        { name: "referrerEmail", label: "Your Email Address" },
        { name: "referrerPhone", label: "Your Phone Number" },
        { name: "companyName", label: "Company / Organization Name" },
        { name: "preferredContactMethod", label: "Preferred Contact Method" },
      ],
    },
    {
      title: "Referral Contact Information",
      fields: [
        { name: "referredName", label: "Referral Name" },
        { name: "referredEmail", label: "Referral Email Address" },
        { name: "referredPhone", label: "Referral Phone Number" },
        { name: "referredCompany", label: "Referral Company / Organization" },
        { name: "relationshipToReferral", label: "Relationship to referral" },
      ],
    },
    {
      title: "Service Interest",
      fields: [
        { name: "serviceInterest", label: "Potential service interest", type: "checkboxGroup" },
        { name: "eventType", label: "Event or Project Type" },
        { name: "eventDate", label: "Event Date", type: "date" },
        { name: "referralContext", label: "Referral context" },
      ],
    },
    {
      title: "Follow-Up Preferences",
      fields: [
        { name: "followUpTiming", label: "When should Kreatethedate follow up?" },
        { name: "followUpInstructions", label: "Follow-up notes or introduction instructions" },
      ],
    },
    { title: "Additional Notes", fields: [{ name: "additionalNotes", label: "Anything else Kreatethedate should know?" }] },
    {
      title: "Permission & Confirmation",
      fields: [
        { name: "referralPermission", label: "I confirm that I have permission to share this referral information with Kreatethedate.", type: "checkbox" },
        { name: "accurateConfirmation", label: "I confirm that the information provided is accurate to the best of my knowledge.", type: "checkbox" },
      ],
    },
  ],
};
